import { useEffect, useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { useParams, useSearchParams } from "react-router-dom";
import { Play, Save, Search, Square } from "lucide-react";
import { toast } from "sonner";
import type { AdminCityPatchRequest, AdminCityPipelineStage, AdminRunStartRequest } from "@citybrews/shared/adminSchemas";
import { apiGet, apiGetEnvelope, apiSend, encodePk } from "../api";
import { PlaceEditorSheet } from "./PlaceEditorPage";
import type { CityPlaceRow, CityRow } from "../types";
import { EmptyState, Field, formatDate, neighborhoodList, numberOrNull, PageError, PAGE_SIZE, pct, STAGE_DESCRIPTIONS, stageForPlace, stageLabel, stateTone, textOrNull, TagEditor } from "../ui-helpers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const fetcher = <T,>(path: string) => apiGet<T>(path);
// Place-scoped stages that can be run across a bulk selection (extract is
// excluded — it is city-wide discovery, never per-place). Mirrors the place
// editor's per-place rerun options.
const bulkStageOptions = [
  { value: "media", label: "Photos & videos" },
  { value: "reviews", label: "Collect reviews" },
  { value: "classify", label: "Fill in details" },
  { value: "hygiene", label: "Quality check" },
  { value: "assemble", label: "Publish" },
  { value: "republish", label: "Republish" },
] as const satisfies ReadonlyArray<{ value: NonNullable<AdminRunStartRequest["stages"]>[number]; label: string }>;
type BulkStage = (typeof bulkStageOptions)[number]["value"];
const cityTabLabels = {
  places: "Live data",
  metadata: "Metadata",
  pipeline: "Pipeline",
} as const;
type CityTab = keyof typeof cityTabLabels;
const cityTabQueryValues = {
  places: null,
  metadata: "metadata",
  pipeline: "pipeline",
} satisfies Record<CityTab, string | null>;
const cityTabOrder = Object.keys(cityTabLabels) as CityTab[];
const cityTabs = {
  fromParams(params: URLSearchParams): CityTab {
    const requestedTab = params.get("tab");
    return requestedTab === "metadata" || requestedTab === "pipeline" ? requestedTab : "places";
  },
  isValue(value: string): value is CityTab {
    return value in cityTabLabels;
  },
  patchParams(params: URLSearchParams, tab: CityTab) {
    const next = new URLSearchParams(params);
    const queryValue = cityTabQueryValues[tab];
    queryValue ? next.set("tab", queryValue) : next.delete("tab");
    next.set("page", "1");
    return next;
  },
};

export function CityPage() {
  const { slug } = useParams();
  if (!slug) return null;
  return <CityDetailPane slug={slug} />;
}

export function CityHeaderSummary({ slug }: { slug: string }) {
  const cityPath = cityRowPath(slug);
  const { data: city, error } = useSWR<CityRow>(cityPath, fetcher);
  return (
    <div className="min-w-0 truncate text-sm text-muted-foreground">
      {error ? "City failed to load" : city ? `${city.country} · ${city.timezone ?? "timezone unset"}` : "Loading city"}
    </div>
  );
}

export function CityHeaderTabs() {
  const [params, setParams] = useSearchParams();
  const tab = cityTabs.fromParams(params);
  return (
    <Tabs value={tab} onValueChange={(value) => {
      if (!cityTabs.isValue(value)) return;
      setParams(cityTabs.patchParams(params, value));
    }}>
      <TabsList variant="line" className="h-9">
        {cityTabOrder.map((value) => <TabsTrigger key={value} value={value}>{cityTabLabels[value]}</TabsTrigger>)}
      </TabsList>
    </Tabs>
  );
}

export function CityDetailPane({ slug }: { slug: string }) {
  const [params, setParams] = useSearchParams();

  const tab = cityTabs.fromParams(params);
  const selectedPlaceId = params.get("place");
  const q = params.get("q") ?? "";
  const category = params.get("category") ?? "all";
  // Coverage gap filters — "which places are missing media / scores / reviews".
  const media = params.get("media") ?? "all";
  const scores = params.get("scores") ?? "all";
  const reviews = params.get("reviews") ?? "all";
  const hygiene = params.get("hygiene") ?? "all";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const offset = (page - 1) * PAGE_SIZE;
  const cityPath = cityRowPath(slug);
  const placesPath = `/admin/api/cities/${slug}/places?limit=${PAGE_SIZE}&offset=${offset}&q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}&media=${media}&scores=${scores}&reviews=${reviews}&hygiene=${hygiene}&sort=name`;
  const { data: city, error: cityError } = useSWR<CityRow>(cityPath, fetcher);
  const pipelinePath = tab === "pipeline" ? `/admin/api/cities/${slug}/pipeline` : null;
  const { data: pipeline, error: pipelineError } = useSWR<{ citySlug: string; stages: AdminCityPipelineStage[] }>(pipelinePath, fetcher, { refreshInterval: 5000 });
  const { data: placesEnvelope, error: placesError } = useSWR(placesPath, () => apiGetEnvelope<CityPlaceRow[]>(placesPath));

  const rows = useMemo(() => (placesEnvelope?.data ?? []).filter((place) => place.isPublished), [placesEnvelope]);
  const total = Number(placesEnvelope?.meta?.total ?? placesEnvelope?.meta?.count ?? rows.length);
  const categories = (placesEnvelope?.meta?.categories as Array<{ category: string; count: number }> | undefined) ?? [];

  // Bulk-select places on the current page → run any place-scoped stage across
  // the selection. Cleared whenever the visible set changes (page/filter/city),
  // since selection is scoped to what's on screen.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkStage, setBulkStage] = useState<BulkStage>("media");
  useEffect(() => { setSelected(new Set()); }, [q, category, media, scores, reviews, hygiene, page, slug]);
  const visibleIds = useMemo(() => rows.map((place) => place.id), [rows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  function toggleOne(id: string) {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  function toggleAllVisible() {
    setSelected((prev) => {
      if (visibleIds.every((id) => prev.has(id))) { const next = new Set(prev); visibleIds.forEach((id) => next.delete(id)); return next; }
      return new Set([...prev, ...visibleIds]);
    });
  }
  async function runStageForSelected() {
    const ids = [...selected];
    if (!ids.length) return;
    const label = bulkStageOptions.find((option) => option.value === bulkStage)?.label ?? bulkStage;
    if (!window.confirm(`Run ${label.toLowerCase()} for ${ids.length} selected place(s) in ${slug}?`)) return;
    const body: AdminRunStartRequest = { citySlug: slug, stages: [bulkStage], placeIds: ids as [string, ...string[]], force: false };
    await apiSend("/admin/api/runs", "POST", body);
    toast.success(`${label} started for ${ids.length} place(s). Progress shows up under Runs.`);
    setSelected(new Set());
    mutate(`/admin/api/cities/${slug}/pipeline`);
  }

  function patchQuery(patch: Record<string, string | null>, replace = false) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace });
  }

  if (cityError || placesError || (tab === "pipeline" && pipelineError)) return <PageError error={cityError || placesError || pipelineError} />;

  return (
    <div className="mx-auto flex h-full min-h-0 max-w-7xl flex-col p-6">
      {tab === "places" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-col gap-2 pb-3 md:flex-row md:flex-wrap md:items-center">
            <select aria-label="Category" className="h-9 w-full rounded-lg border bg-background px-2 text-sm md:w-48" value={category} onChange={(event) => patchQuery({ category: event.target.value === "all" ? null : event.target.value, page: "1" })}>
              <option value="all">All categories</option>
              {categories.map((entry) => <option key={entry.category} value={entry.category}>{entry.category} ({entry.count})</option>)}
            </select>
            <select aria-label="Media" className="h-9 rounded-lg border bg-background px-2 text-sm" value={media} onChange={(event) => patchQuery({ media: event.target.value === "all" ? null : event.target.value, page: "1" })}>
              <option value="all">Any media</option>
              <option value="has">Has media</option>
              <option value="not-found">Media not found</option>
              <option value="pending">Media not run</option>
            </select>
            <CoverageFilter label="Scores" value={scores} onChange={(v) => patchQuery({ scores: v === "all" ? null : v, page: "1" })} />
            <CoverageFilter label="Reviews" value={reviews} onChange={(v) => patchQuery({ reviews: v === "all" ? null : v, page: "1" })} />
            <select aria-label="Quality check" className="h-9 rounded-lg border bg-background px-2 text-sm" value={hygiene} onChange={(event) => patchQuery({ hygiene: event.target.value === "all" ? null : event.target.value, page: "1" })}>
              <option value="all">Any quality check</option>
              <option value="pass">Quality check: pass</option>
              <option value="flag">Quality check: flag</option>
              <option value="cut">Quality check: cut</option>
              <option value="missing">Quality check: missing</option>
            </select>
            <div className="relative w-full md:ml-auto md:w-72">
              <Search className="absolute left-2 top-2 size-4 text-muted-foreground" />
              <Input className="pl-8" defaultValue={q} placeholder="Search places" onKeyDown={(event) => {
                if (event.key === "Enter") patchQuery({ q: event.currentTarget.value, page: "1" });
              }} />
            </div>
          </div>
          {selected.size > 0 && (
            <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <span className="font-medium">{selected.size} selected</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
                <select aria-label="Stage to run" className="h-8 w-36 rounded-lg border bg-background px-2 text-sm" value={bulkStage} onChange={(event) => setBulkStage(event.target.value as BulkStage)}>
                  {bulkStageOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <Button size="sm" onClick={() => runStageForSelected().catch((error) => toast.error(error instanceof Error ? error.message : String(error)))}>
                  <Play /> Run for selected ({selected.size})
                </Button>
              </div>
            </div>
          )}
          <div className="flex min-h-0 flex-1 flex-col border-t">
            {!placesEnvelope ? <Skeleton className="h-96 w-full" /> : rows.length ? (
              <>
                <div className="min-h-0 flex-1 overflow-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead className="w-8"><input type="checkbox" aria-label="Select all on page" className="size-4 accent-primary align-middle" checked={allVisibleSelected} onChange={toggleAllVisible} /></TableHead><TableHead>Name</TableHead><TableHead>Neighborhood</TableHead><TableHead>Category</TableHead><TableHead>Rating</TableHead><TableHead>Reviews</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {rows.map((place) => {
                        const stage = stageForPlace(place);
                        return (
                          <TableRow key={place.id} className="cursor-pointer" onClick={() => patchQuery({ place: place.id })}>
                            <TableCell className="w-8" onClick={(event) => event.stopPropagation()}>
                              <input type="checkbox" aria-label={`Select ${place.name}`} className="size-4 accent-primary align-middle" checked={selected.has(place.id)} onChange={() => toggleOne(place.id)} />
                            </TableCell>
                            <TableCell className="font-medium">{place.name}</TableCell>
                            <TableCell>{place.neighborhood || "Unassigned"}</TableCell>
                            <TableCell>{place.category}</TableCell>
                            <TableCell>{place.rating?.toFixed(1) ?? "—"}</TableCell>
                            <TableCell>{place.reviewCount?.toLocaleString() ?? "0"}</TableCell>
                            <TableCell><Badge variant="outline">{stage}</Badge></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex shrink-0 items-center justify-between border-t bg-background/95 py-3 text-sm text-muted-foreground backdrop-blur">
                  <span>{offset + 1}-{Math.min(offset + rows.length, total)} of {total}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" disabled={page <= 1} onClick={() => patchQuery({ page: String(page - 1) })}>Previous</Button>
                    <Button variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => patchQuery({ page: String(page + 1) })}>Next</Button>
                  </div>
                </div>
              </>
            ) : <EmptyState title="No places" description="Adjust the search or category." />}
          </div>
        </div>
      )}
      {tab === "metadata" && <CityMetadata city={city} cityPath={cityPath} />}
      {tab === "pipeline" && (
        <section className="grid gap-4">
          <div>
            <h2 className="text-lg font-semibold">Pipeline controls</h2>
            <p className="mt-1 text-sm text-muted-foreground">Run machinery for this city. Per-place step history lives in Runs.</p>
          </div>
          {!pipeline ? <Skeleton className="h-28" /> : <PipelineStrip slug={slug} stages={pipeline.stages} />}
        </section>
      )}
      <PlaceEditorSheet slug={slug} placeId={selectedPlaceId} onOpenChange={(open) => { if (!open) patchQuery({ place: null }, true); }} />
    </div>
  );
}

function cityRowPath(slug: string) {
  return `/admin/api/tables/cities/rows/${encodePk({ slug })}`;
}

// "Has X / missing X" filter — the point is finding the gaps (every place
// without media, without a score) so they can be bulk-selected and re-run.
function CoverageFilter({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <select aria-label={`${label} coverage`} className="h-9 rounded-lg border bg-background px-2 text-sm" value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="all">Any {label.toLowerCase()}</option>
      <option value="with">Has {label.toLowerCase()}</option>
      <option value="without">Missing {label.toLowerCase()}</option>
    </select>
  );
}

function PipelineStrip({ slug, stages }: { slug: string; stages: AdminCityPipelineStage[] }) {
  async function run(stage: string, force = false) {
    const body: AdminRunStartRequest = { citySlug: slug, stages: [stage as NonNullable<AdminRunStartRequest["stages"]>[number]], force };
    await apiSend("/admin/api/runs", "POST", body);
    toast.success(`${stageLabel(stage)} started. Progress shows up under Runs.`);
    mutate(`/admin/api/cities/${slug}/pipeline`);
  }
  async function cancel(stage: AdminCityPipelineStage) {
    if (!stage.latestRunId) return toast.info("No cancellable run id for this stage.");
    await apiSend(`/admin/api/runs/${stage.latestRunId}/cancel`, "POST", { reason: "admin cancel" });
    toast.success("Run cancelled");
    mutate(`/admin/api/cities/${slug}/pipeline`);
  }
  if (stages.length === 0) return <div className="grid gap-2">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-12" />)}</div>;
  return (
    <div className="divide-y rounded-lg border">
      {stages.map((stage) => {
        const running = ["running", "queued", "waiting"].includes(stage.state);
        const { done, total, label } = stage.coverage;
        const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
        const lastRun = stage.updatedAt ?? stage.finishedAt ?? stage.startedAt;
        return (
          <div key={stage.stage} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
            <div className="flex w-64 shrink-0 items-center gap-2" title={`${STAGE_DESCRIPTIONS[stage.stage] ?? ""} (${stage.stage})`.trim()}>
              <span className={`size-2 shrink-0 rounded-full ${stateTone(stage.state)}`} />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{stageLabel(stage.stage)}</div>
                {STAGE_DESCRIPTIONS[stage.stage] && <div className="truncate text-xs text-muted-foreground">{STAGE_DESCRIPTIONS[stage.stage]}</div>}
              </div>
            </div>

            {/* Real coverage from the data — not run bookkeeping. */}
            <div className="flex min-w-[260px] flex-1 items-center gap-3">
              {total > 0 ? (
                <>
                  <div className="h-1.5 w-full max-w-[180px] shrink-0 overflow-hidden rounded-full bg-muted">
                    <div className={`h-full rounded-full ${pct >= 99 ? "bg-emerald-500" : pct > 0 ? "bg-sky-500" : "bg-muted"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="whitespace-nowrap text-xs tabular-nums">
                    <span className="font-medium">{done.toLocaleString()}</span>
                    <span className="text-muted-foreground"> / {total.toLocaleString()} {label}</span>
                  </span>
                </>
              ) : (
                <span className="whitespace-nowrap text-xs tabular-nums"><span className="font-medium">{done.toLocaleString()}</span> <span className="text-muted-foreground">{label}</span></span>
              )}
            </div>

            <div className="w-32 shrink-0 text-xs text-muted-foreground">{lastRun ? formatDate(lastRun) : "Never run"}</div>
            <div className="flex shrink-0 gap-1">
              {running ? <Button size="xs" variant="destructive" onClick={() => cancel(stage).catch(showError)}><Square /> Stop</Button> : <Button size="xs" variant="outline" onClick={() => run(stage.stage).catch(showError)}><Play /> Run</Button>}
              <Button size="xs" variant="ghost" onClick={() => run(stage.stage, true).catch(showError)}>Force</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CityMetadata({ city, cityPath }: { city?: CityRow; cityPath: string }) {
  const [form, setForm] = useState({ name: "", country: "", countryCode: "", cityCode: "", centerLat: "", centerLng: "", defaultZoom: "", timezone: "", flagEmoji: "", neighborhoods: [] as string[] });
  useEffect(() => {
    if (!city) return;
    setForm({
      name: city.name ?? "",
      country: city.country ?? "",
      countryCode: city.countryCode ?? "",
      cityCode: city.cityCode ?? "",
      centerLat: city.centerLat?.toString() ?? "",
      centerLng: city.centerLng?.toString() ?? "",
      defaultZoom: city.defaultZoom?.toString() ?? "",
      timezone: city.timezone ?? "",
      flagEmoji: city.flagEmoji ?? "",
      neighborhoods: neighborhoodList(city.neighborhoods),
    });
  }, [city]);
  if (!city) return <Skeleton className="h-96" />;
  const currentCity = city;
  async function save() {
    const body: AdminCityPatchRequest = {
      name: form.name,
      country: form.country,
      countryCode: form.countryCode,
      cityCode: form.cityCode,
      centerLat: numberOrNull(form.centerLat),
      centerLng: numberOrNull(form.centerLng),
      defaultZoom: numberOrNull(form.defaultZoom),
      timezone: textOrNull(form.timezone),
      flagEmoji: textOrNull(form.flagEmoji),
      neighborhoods: form.neighborhoods,
    };
    await apiSend(`/admin/api/cities/${currentCity.slug}`, "PATCH", body);
    toast.success("City saved");
    mutate(cityPath);
  }
  return (
    <section className="grid max-w-3xl gap-4">
      <div>
        <h2 className="text-lg font-semibold">Metadata</h2>
        <p className="mt-1 text-sm text-muted-foreground">Canonical city fields.</p>
      </div>
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Country"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
          <Field label="Country code"><Input value={form.countryCode} onChange={(e) => setForm({ ...form, countryCode: e.target.value })} /></Field>
          <Field label="City code"><Input value={form.cityCode} onChange={(e) => setForm({ ...form, cityCode: e.target.value })} /></Field>
          <Field label="Lat"><Input value={form.centerLat} onChange={(e) => setForm({ ...form, centerLat: e.target.value })} /></Field>
          <Field label="Lng"><Input value={form.centerLng} onChange={(e) => setForm({ ...form, centerLng: e.target.value })} /></Field>
          <Field label="Zoom"><Input value={form.defaultZoom} onChange={(e) => setForm({ ...form, defaultZoom: e.target.value })} /></Field>
          <Field label="Timezone"><Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></Field>
        </div>
        <TagEditor label="Neighborhoods" values={form.neighborhoods} suggestions={[]} onChange={(neighborhoods) => setForm({ ...form, neighborhoods })} />
        <Button onClick={() => save().catch(showError)}><Save /> Save metadata</Button>
      </div>
    </section>
  );
}

function showError(error: unknown) {
  toast.error(error instanceof Error ? error.message : "Request failed");
}
